import {
  useRef,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react';
import {
  useResizeObserver,
  useMutationObserver,
  useGetRefById,
} from '@fortress-technology-solutions/fortress-component-library/hooks';
import { styled } from '@fortress-technology-solutions/fortress-component-library/utils';
import {
  APP_BAR_HEIGHT,
  PADDING,
  MUTATION_OBSERVER_OPTIONS,
} from './constants';
import { debounce } from 'lodash';
import { calculateChildrenHeight, calculatePageHeight } from './utils';

const Container = styled('div')({
  height: '100%',
});

const INITIAL_STATE_ONLY = false;

/**
 * TableLayout Component
 *
 * A layout wrapper that dynamically calculates the available height for a table (or other content)
 * based on the window size and other elements on the page. It ensures the content fits within
 * the viewport or parent container without unnecessary scrolling of the entire page.
 *
 * @component
 *
 * @param {Object} props
 * @param {Function} props.children - Render prop that receives the calculated `tableHeight`.
 *                                    Example: `(tableHeight) => <Table height={tableHeight} ... />`
 * @param {string} props.name - Unique identifier for the component instance. Used for the container ID.
 * @param {string[]} [props.watch] - Array of element IDs to watch for resize events.
 *                                   If these elements change size, the table height is recalculated.
 * @param {Object} [props.sx] - MUI system props or style object to extend the container styles.
 * @param {number} [props.childrenElementsHeight=0] - Height of other elements inside the TableLayout that should be
 * subtracted from the available table height.
 * @param {number} [props.containerElementsHeight=0] - Height of elements outside the TableLayout (like headers) that
 * should be subtracted from the container height.
 *
 * @returns {JSX.Element} The TableLayout container.
 *
 * @example
 * <TableLayout
 *   name="my-table"
 *   watch={['filter-bar']}
 *   childrenElementsHeight={50}
 * >
 *   {(tableHeight) => (
 *     <MyTable height={tableHeight} data={data} />
 *   )}
 * </TableLayout>
 */
const TableLayout = ({
  children,
  name,
  watch,
  sx,
  childrenElementsHeight = 0,
  containerElementsHeight = 0,
  useMaxHeight = false,
}) => {
  const ref = useRef(null);
  const previousParentHeight = useRef(null);
  // elements that take up space on page
  const homeTabRef = useGetRefById('home-tab');
  const homeTabContentRef = useGetRefById('home-tab-content');

  const [containerHeight, setContainerHeight] = useState(
    window.innerHeight - (APP_BAR_HEIGHT + containerElementsHeight),
  );
  const [tableHeight, setTableHeight] = useState(
    containerHeight - (childrenElementsHeight + PADDING),
  );

  const calculatePageHeightCallback = useCallback(
    (body) => {
      debounce(() => {
        const containerHeight = calculatePageHeight({
          homeTabContentRef,
          homeTabRef,
          previousParentHeight,
          body,
        });

        if (!isNaN(containerHeight) && !INITIAL_STATE_ONLY) {
          setContainerHeight((prevContainerHeight) =>
            containerHeight > 0 ? containerHeight : prevContainerHeight,
          );
        }
      }, 300)();
    },
    [homeTabContentRef, homeTabRef],
  );

  const calculateChildrenHeightCallback = useCallback(
    ({ children }) => {
      debounce(() => {
        const tableHeight = calculateChildrenHeight({
          children,
          containerHeight,
          name,
        });

        if (!isNaN(tableHeight) && !INITIAL_STATE_ONLY) {
          setTableHeight((prevTableHeight) =>
            tableHeight > 0 ? tableHeight : prevTableHeight,
          );
        }
      }, 300)();
    },
    [containerHeight, name],
  );

  // mutation observer to watch for changes in children
  useMutationObserver(
    ref,
    useCallback(
      (mutationsList, observer) => {
        // If the target node or its children's attributes change, do something
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
          } else if (mutation.type === 'attributes') {
            if (!ref.current) return;
            calculateChildrenHeightCallback(ref.current);
          }
        }
      },
      [calculateChildrenHeightCallback],
    ),
    MUTATION_OBSERVER_OPTIONS,
  );

  useResizeObserver(
    document.body,
    useCallback(
      (entry) => {
        if (previousParentHeight.current === entry.target.clientHeight) {
          return;
        }
        calculatePageHeightCallback(entry.target);
      },
      [calculatePageHeightCallback],
    ),
  );

  useEffect(() => {
    if (!ref.current) return;
    calculateChildrenHeightCallback(ref.current);
  }, [calculateChildrenHeightCallback, containerHeight]);

  useLayoutEffect(() => {
    if (!ref.current?.children) return;

    // resize observe the children that match the watch ids
    const children = ref.current.children;
    const resizeObserver = new ResizeObserver((entries) => {
      if (!ref.current) return;
      calculateChildrenHeightCallback(ref.current);
    });

    // Also watch for elements inside children that match the watch ids
    if (watch) {
      watch.forEach((id) => {
        const element = document.getElementById(id);
        if (element) {
          resizeObserver.observe(element);
        }
      });
    }

    Array.from(children).forEach((child) => {
      if (watch?.includes(child.id)) {
        resizeObserver.observe(child);
      }
    });
  }, [calculateChildrenHeightCallback, watch]);

  return (
    <Container
      ref={ref}
      id={`table-layout_${name}`}
      sx={{
        overflow: 'hidden',
        height: useMaxHeight ? 'auto' : containerHeight - 1,
        maxHeight: useMaxHeight ? containerHeight - 1 : undefined,
        padding: 2,
        ...sx,
      }}
    >
      {children(tableHeight)}
    </Container>
  );
};

export default TableLayout;
